import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { ProjectElement } from '../../resources/element/project-element.interface';
import { IndexView } from './project-info.interface';

/**
 * Project info helper service.
 */
@Injectable({
  providedIn: 'root'
})
export class ProjectInfoService {

  /** Default inline edit mode. */
  private readonly INLINE_EDIT_ACTIVE = true;

  /**
   * Index views.
   *
   * @note `_id: 1` is reserved for a possible *submodules* view, where only the elements
   *  are hidden. Shouldn't be hard to implement, but currently there's no reason to.
   */
  private readonly VIEWS = [{
    _id: 0,
    key: 'all',
    label: 'infoIndexViewAll'
  }, {
    _id: 2,
    key: 'modules',
    label: 'infoIndexViewModules'
  }];

  /** Default index view. */
  private readonly VIEWS_DEFAULT = 0;

  /** Toggle for inline edit mode. Defaults to `true` */
  protected _inlineEdit$ = new BehaviorSubject<boolean>(this.INLINE_EDIT_ACTIVE);

  /** Current inline edit mode. */
  public inlineEdit$ = this._inlineEdit$.asObservable();

  /** Visible elements subject. */
  protected _visibleElement$ = new BehaviorSubject<ProjectElement['_id'][]>([]);

  /** Visible elements observable. */
  public visibleElement$ = this._visibleElement$.asObservable();

  /** Current active view. */
  protected _view$ = new BehaviorSubject<IndexView>(this.getIndexViewDefault());

  /** Current active view observable. */
  public view$ = this._view$.asObservable();

  /**
   * Get the default index view.
   *
   * @returns The current view of the index.
   */
  public getIndexViewDefault(): IndexView {
    return this.VIEWS[this.VIEWS_DEFAULT];
  }

  /**
   * Obtain list of all currently available views.
   *
   * @returns List of available index views.
   */
  public getIndexViews(): IndexView[] {
    return this.VIEWS;
  }

  /**
   * Set the current active index view.
   *
   * @param selectedId Selected index view id.
   */
  public setIndexView(selectedId: IndexView['_id']): void {
    this._view$.next(this.VIEWS.find(v => v._id === selectedId));
  }

  /**
   * Toggle inline edit mode.
   *
   * @param active Current status.
   */
  public setInlineEdit(active: boolean): void {
    this._inlineEdit$.next(active);
  }

  /**
   * Update observable with the currently visible elements.
   *
   * @param elements List of ids of currently visible elements.
   */
  public setVisibleElements(elements: ProjectElement['_id'][]) {
    if (elements.length) {
      this._visibleElement$.next(elements);
    }
  }
}
